import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const LawnCareSlowMobile = () => {
  const symptoms = [
    "Your website takes more than 3 seconds to load on phones",
    "Google PageSpeed Insights shows a mobile score under 50",
    "Visitors are bouncing before the page fully loads",
    "Images appear blurry or take forever to load",
    "Text and buttons are too small to tap on mobile",
    "Your site looks broken on certain phone models"
  ];

  const rootCauses = [
    { cause: "Unoptimized images (too large file sizes)", impact: "Images over 500KB can add 5+ seconds to load time" },
    { cause: "No image compression or lazy loading", impact: "All images load at once, overwhelming mobile connections" },
    { cause: "Slow or cheap hosting provider", impact: "Budget hosting can't handle traffic spikes or mobile requests" },
    { cause: "Too many plugins or scripts", impact: "Each plugin adds loading time, especially on WordPress" },
    { cause: "Not using a CDN (content delivery network)", impact: "Content travels long distances, slowing load times" },
    { cause: "Large video files auto-playing", impact: "Videos can add 10+ seconds to mobile load times" },
    { cause: "Desktop-first design shrunken for mobile", impact: "Elements don't resize properly, creating poor UX" },
    { cause: "Render-blocking CSS and JavaScript", impact: "Page can't display until heavy files finish loading" }
  ];

  const fixSteps = [
    { step: "Compress all images to under 200KB", details: "Use tools like TinyPNG or Squoosh to reduce file sizes without losing quality" },
    { step: "Enable lazy loading for images", details: "Images load only when visitors scroll to them, speeding up initial load" },
    { step: "Upgrade to a fast hosting provider", details: "Quality hosts like Cloudways or WP Engine make a massive difference" },
    { step: "Remove unnecessary plugins", details: "Audit your plugins and remove anything you don't actively use" },
    { step: "Use a CDN for faster content delivery", details: "Cloudflare's free CDN can reduce load times by 50% or more" },
    { step: "Defer non-critical JavaScript", details: "Let the visible page load first, then load scripts" },
    { step: "Test on actual mobile devices", details: "Google's Mobile-Friendly Test and real phone testing catch issues simulators miss" }
  ];

  const faqs = [
    { question: "How fast should my lawn care website load on mobile?", answer: "Under 3 seconds. Google's research shows that 53% of mobile visitors leave if a page takes longer than 3 seconds to load. For lawn care specifically, most searches happen on mobile, so speed directly impacts leads." },
    { question: "Why does mobile speed matter for lawn care websites?", answer: "Over 60% of lawn care searches happen on mobile devices. Homeowners searching while standing in their yard want quick answers. A slow mobile experience means lost leads to faster competitors." },
    { question: "How do I check my lawn care website's mobile speed?", answer: "Use Google PageSpeed Insights (free) to test your site. Enter your URL and view the mobile score. Aim for 70+ on mobile. The tool also provides specific recommendations for improvement." },
    { question: "What's the biggest cause of slow lawn care websites?", answer: "Unoptimized images are the #1 culprit. Many lawn care websites feature large before/after photos and gallery images that haven't been compressed. A single unoptimized image can add 3-5 seconds to load time." },
    { question: "Should I hire someone to speed up my website?", answer: "If your PageSpeed score is under 50, professional help is recommended. Speed optimization involves technical skills like code modification, server configuration, and image processing. DIY fixes can help, but comprehensive optimization requires expertise." },
    { question: "Will speeding up my website help with SEO?", answer: "Yes. Google uses page speed as a ranking factor, especially for mobile searches. A faster website not only keeps visitors but also ranks higher in search results, bringing you more potential leads." },
    { question: "How much does website speed affect conversions?", answer: "Significantly. Every additional second of load time reduces conversions by 7%. A website that loads in 6 seconds instead of 3 could be losing 21% of potential leads just from speed alone." },
    { question: "Is my hosting provider slowing down my website?", answer: "Possibly. Budget hosting ($3-10/month) often can't handle traffic effectively. Quality managed hosting ($25-50/month) provides faster servers, better caching, and improved mobile performance." },
    { question: "Should I remove images to speed up my lawn care website?", answer: "No—optimize them instead. Before/after photos and work examples are crucial for lawn care websites. Compress images to under 200KB each and use lazy loading so they don't slow initial page load." },
    { question: "What's a good mobile PageSpeed score for lawn care websites?", answer: "Aim for 70+ on mobile. Scores of 50-69 are acceptable but should be improved. Scores under 50 indicate serious problems that are likely costing you leads." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/lawn-care-website-design/", label: "Lawn Care Website Design" },
    { to: "/website-fixes/lawn-care-website-not-getting-leads/", label: "Website Not Getting Leads" },
    { to: "/lawn-care-website-seo/", label: "Lawn Care Website SEO" }
  ];

  return (
    <>
      <SEO
        title="Lawn Care Website Slow on Mobile? Fix It Now | Leaf Rush Marketing"
        description="Is your lawn care website slow on mobile? Learn why speed matters and get step-by-step fixes to stop losing leads to faster competitors."
        url="https://www.leafrushmarketing.com/website-fixes/lawn-care-website-slow-mobile/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/lawn-care-website-slow-mobile/"
        title="Lawn Care Website Slow on Mobile? Fix It Now"
        description="Is your lawn care website slow on mobile? Learn why speed matters and get step-by-step fixes."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Lawn Care Website Slow on Mobile? Fix It Now
            </h1>
            <p className="text-xl text-muted-foreground">
              Over 60% of your potential customers are searching on phones. If your site is slow, you're losing them.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Mobile Speed Is Killing Leads" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: What's Slowing Your Site Down</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Speed Up Your Mobile Website" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Why Mobile Speed Is Non-Negotiable</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Picture this: A homeowner notices their lawn needs work. They grab their phone, search "lawn care near me," and tap your website. If it takes more than 3 seconds to load, they hit back and call your competitor instead.
              </p>
              <p>
                This happens dozens of times per month for slow lawn care websites. <strong>Speed isn't about user preference—it's about whether you get the lead or not.</strong>
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, every website we build is optimized for mobile speed from day one. We use compressed images, fast hosting, and mobile-first design to ensure your site loads in under 3 seconds on any device.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get a Free Speed Audit" />
        </div>
      </article>
    </>
  );
};

export default LawnCareSlowMobile;
